import { useEffect, useRef } from 'react';
import type { CaptureSource } from '@/lib/types';
import type { CaptureSequence } from '../types';
import {
  requestPostureHandoff,
  type PostureHandoffPayload,
} from '@/components/remote-assessment/lib/posture-handoff';

export const REMOTE_HANDOFF_PARAM = 'remoteHandoff';

/** True when Posture Analysis was opened from Remote Assessment "Review & save". */
export function isRemoteHandoffSearch(search: string): boolean {
  return new URLSearchParams(search).has(REMOTE_HANDOFF_PARAM);
}

/**
 * Posture Analysis opened from the Remote Assessment workbench (`?remoteHandoff=<id>`): once the
 * image pose model is ready, fetch the stills from the workbench tab and queue each into its view
 * as `screen-share`, so detection, landmark adjust, and save run as usual. Runs once per id.
 */
export function useRemotePostureHandoff({
  search,
  ready,
  clientId,
  queueImage,
  request = (id) => requestPostureHandoff(id),
  onError,
}: {
  search: string;
  ready: boolean;
  clientId: string | undefined;
  queueImage: (dataUrl: string, view: CaptureSequence, options: { captureSource: CaptureSource }) => void;
  request?: (id: string) => Promise<PostureHandoffPayload | null>;
  onError: (message: string) => void;
}): void {
  const handledRef = useRef<string | null>(null);
  const id = new URLSearchParams(search).get(REMOTE_HANDOFF_PARAM);

  useEffect(() => {
    // Wait for the client too: checking the handoff against an unloaded client would skip the
    // wrong-client guard, and handledRef would then never re-check this id.
    if (!id || !ready || !clientId || handledRef.current === id) return;
    handledRef.current = id;
    void request(id).then((payload) => {
      if (!payload) {
        onError('Could not load the Remote Assessment stills. Keep the workbench tab open and try Review & save again.');
        return;
      }
      if (payload.clientId !== clientId) {
        onError('These stills were captured for a different client. Select that client and try again.');
        return;
      }
      for (const [view, dataUrl] of Object.entries(payload.views) as [CaptureSequence, string][]) {
        if (dataUrl) queueImage(dataUrl, view, { captureSource: 'screen-share' });
      }
    });
  }, [id, ready, clientId, queueImage, request, onError]);
}
